import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Download, Wrench } from "lucide-react";
import { toast } from "sonner";

const PROGRAMS = ["", "ambassador", "panda_pack"];

export const SignupsView = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [program, setProgram] = useState("");
  const [inviter, setInviter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [totals, setTotals] = useState([]);
  const [recon, setRecon] = useState(null);

  const load = useCallback(async (p = {}) => {
    setError(false);
    try {
      const { data } = await api.get("/admin/referrals/signups", { params: {
        q: (p.q ?? q) || undefined, program: (p.program ?? program) || undefined,
        referrer: (p.inviter ?? inviter) || undefined,
        date_from: (p.from ?? from) || undefined, date_to: (p.to ?? to) || undefined,
      }});
      setRows(data.signups); setTotal(data.total);
    } catch { setError(true); }
  }, [q, program, inviter, from, to]);

  useEffect(() => {
    load({});
    api.get("/admin/referrals/totals").then(({ data }) => setTotals(data.totals || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecon = async () => {
    try {
      const { data } = await api.get("/admin/referrals/reconciliation");
      setRecon(data);
    } catch { toast.error("Couldn't load reconciliation preview."); }
  };
  const resolveConflict = async (row, claim) => {
    if (!window.confirm(`Credit @${claim.referrer_handle} (${claim.program}) as ${row.display_name}'s one inviter? Other claims will be rejected.`)) return;
    try {
      await api.post(`/admin/referrals/conflicts/${row.user_id}/resolve`, { referrer_id: claim.referrer_id });
      toast.success(`@${claim.referrer_handle} credited for ${row.display_name}.`);
      load({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed."); }
  };

  const applyRecon = async () => {
    const reviewed = recon.proposed_repairs.filter(r => r.inviter_exists).map(r => r.user_id);
    if (!window.confirm(`Create ${reviewed.length} evidence-backed ambassador rows?`)) return;
    try {
      const { data } = await api.post("/admin/referrals/reconciliation/apply", { user_ids: reviewed });
      toast.success(`Repaired ${data.created} historical referrals.`);
      setRecon(null); load({});
    } catch { toast.error("Repair failed."); }
  };

  return (
    <div className="space-y-4" data-testid="admin-referral-signups">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="signups-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load({})} placeholder="Member handle/email…"
          className="max-w-[180px] bg-zinc-950 border-white/10 rounded-none" />
        <Input data-testid="signups-inviter" value={inviter} onChange={(e) => setInviter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load({})} placeholder="Inviter @handle"
          className="max-w-[150px] bg-zinc-950 border-white/10 rounded-none" />
        <select data-testid="signups-program" value={program}
          onChange={(e) => { setProgram(e.target.value); load({ program: e.target.value }); }}
          className="bg-zinc-950 border border-white/10 text-sm px-2 py-2 text-zinc-300">
          {PROGRAMS.map((p) => <option key={p} value={p}>{p || "All programs"}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); load({ from: e.target.value }); }}
          className="bg-zinc-950 border border-white/10 text-xs px-2 py-2 text-zinc-400" data-testid="signups-date-from" />
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); load({ to: e.target.value }); }}
          className="bg-zinc-950 border border-white/10 text-xs px-2 py-2 text-zinc-400" data-testid="signups-date-to" />
        <span className="text-xs text-zinc-500">{total} signups</span>
        <a data-testid="signups-export" href={`${api.defaults.baseURL}/admin/referrals/signups/export`}
          className="ml-auto inline-flex items-center gap-1.5 text-xs border border-white/15 px-3 py-2 text-zinc-300 hover:bg-white/5">
          <Download size={13} /> CSV
        </a>
        <Button data-testid="signups-reconcile-btn" onClick={loadRecon} variant="ghost"
          className="h-8 rounded-none border border-amber-900/50 text-amber-300 text-xs px-2">
          <Wrench size={12} className="mr-1" /> Reconcile
        </Button>
      </div>

      {recon && (
        <div className="card-surface p-4 border-amber-500/30" data-testid="reconciliation-panel">
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">Repair preview — evidence-backed only</div>
          <div className="text-sm text-zinc-400 mb-2">
            {recon.proposed_repairs.length} member(s) with <code>referred_by</code> evidence but no referral row · {recon.orphaned_rows.length} orphaned row(s) flagged (deleted accounts, untouched)
          </div>
          {(recon.conflicts?.length > 0 || recon.duplicate_rows?.length > 0) && (
            <div className="text-xs text-amber-400/90 mb-2" data-testid="reconciliation-conflicts">
              {recon.conflicts?.length || 0} conflicting attribution(s) and {recon.duplicate_rows?.length || 0} duplicate row group(s) flagged — owner decision required, never auto-repaired.
            </div>
          )}
          {recon.proposed_repairs.slice(0, 10).map((r) => (
            <div key={r.user_id} className="text-xs text-zinc-500 py-0.5">
              {r.display_name} ← invited by @{r.invited_by || "?"} · {r.signed_up_at?.slice(0, 10)} · {r.evidence}
            </div>
          ))}
          {recon.proposed_repairs.length > 0 && (
            <Button data-testid="reconciliation-apply-btn" onClick={applyRecon}
              className="btn-crimson rounded-none h-8 text-xs px-3 mt-2">Apply repairs</Button>
          )}
        </div>
      )}

      {totals.length > 0 && (
        <div className="card-surface p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Totals per inviter</div>
          <div className="flex flex-wrap gap-2">
            {totals.slice(0, 12).map((t) => (
              <span key={t.referrer_id} className="text-xs bg-zinc-900 border border-white/10 px-2 py-1 text-zinc-300"
                data-testid={`inviter-total-${t.handle}`}>
                @{t.handle || "?"} · {t.total} ({t.qualified} qualified)
              </span>
            ))}
          </div>
        </div>
      )}

      {error ? (
        <div className="card-surface p-6 text-center text-rose-400 text-sm" data-testid="signups-load-error">
          Couldn't load signups — refresh to retry. (Totals above may be stale.)
        </div>
      ) : (
        <div className="overflow-x-auto card-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="p-3">Member</th><th className="p-3">Signed up</th><th className="p-3">Method</th>
                <th className="p-3">Invited by</th><th className="p-3">Program</th><th className="p-3">Code</th>
                <th className="p-3">Verified</th><th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5" data-testid={`signup-row-${r.handle}`}>
                  <td className="p-3"><Link to={`/u/${r.id}`} className="text-rose-400 hover:underline">{r.display_name}</Link></td>
                  <td className="p-3 text-xs text-zinc-500">{r.signed_up_at?.slice(0, 10)}</td>
                  <td className="p-3 text-xs text-zinc-500">{r.auth_provider}</td>
                  <td className="p-3">
                    {r.invited_by
                      ? <Link to={`/u/${r.invited_by}`} className="text-emerald-300 hover:underline" data-testid={`invited-by-${r.handle}`}>@{r.invited_by}</Link>
                      : r.referral_status === "pending_review"
                        ? <span className="text-amber-400 text-xs" data-testid={`conflict-${r.handle}`}>Conflict — pending review</span>
                        : <span className="text-zinc-600 text-xs">No recorded referrer</span>}
                    {r.referral_status === "pending_review" && r.conflict?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.conflict.map((c) => (
                          <button key={c.referrer_id} data-testid={`resolve-${r.handle}-${c.referrer_handle}`}
                            onClick={() => resolveConflict(r, c)}
                            className="text-[10px] border border-amber-700/50 text-amber-300 px-1.5 py-0.5 hover:bg-amber-900/20">
                            Credit @{c.referrer_handle} ({c.program})
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-zinc-400">{r.program || (r.referral_status === "pending_review" ? "in dispute" : "—")}</td>
                  <td className="p-3 text-xs text-zinc-500">{r.code || "—"}</td>
                  <td className="p-3 text-xs">{r.email_verified ? <span className="text-emerald-300">yes</span> : <span className="text-zinc-600">no</span>}</td>
                  <td className="p-3 text-xs text-zinc-400">{r.referral_status || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td className="p-6 text-center text-zinc-600 text-xs" colSpan={8}>No signups match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SignupsView;
